import { Link, useNavigate } from 'react-router';
import { Button } from '@xiltrion/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@xiltrion/ui/select';
import { Skeleton } from '@xiltrion/ui/skeleton';
import { Icono, type NombreIcono } from '@xiltrion/ui/icono';
import { Item, ItemActions, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@xiltrion/ui/item';
import { useSession } from '@/app/session';
import { useMyDashboard, useTasks } from '@/features/tasks/api';
import { SupportTaskRow, usePendingSupport } from '@/features/support/SupportTaskRow';
import { useAreaCounts, useDistribution } from '@/features/assignments/api';
import { flattenUnits, useUnitsTree } from '@/features/cases/lookups';
import { useLocalStorage, dueGroup, ACTION_LABEL } from '@/features/cases/lib';
import { NewCaseButton } from '@/features/cases/NewCaseButton';
import { Card, DueBadge, EmptyState, ErrorState, PageHeader, PriorityTag } from '@/components/ui';
import type { Task } from '@/api/types';

/** Inicio: answers “¿qué tengo pendiente / vencido / requiere atención / bloqueado / acaba de llegar?”. */
export function DashboardPage() {
  const { me, admin, supervisor } = useSession();
  const leader = admin || supervisor;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Buenos días' : hour < 18 ? 'Buenas tardes' : 'Buenas noches';
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={`${greeting}, ${me?.displayName?.split(' ')[0] ?? ''}`} subtitle="Lo que necesita tu atención hoy." actions={<NewCaseButton />} />
      <TerminosSection />
      <InstrumentadorSection />
      {leader && <AreaSection />}
    </div>
  );
}

function StatCard({ label, value, tone, to, icon, hint }: { label: string; value: number | undefined; tone: 'info' | 'warning' | 'critical' | 'ok' | 'neutral'; to: string; icon: NombreIcono; hint?: string }) {
  const cls = { info: 'border-primary/30 text-primary', warning: 'border-warning/40 text-warning', critical: 'border-destructive/30 text-destructive', ok: 'border-success/30 text-success', neutral: 'border-border text-foreground' }[tone];
  return (
    <Link to={to} className={`no-underline bg-card border rounded-lg p-4 flex flex-col gap-1 shadow-sm hover:shadow-md transition-shadow ${cls}`} aria-label={`${label}: ${value ?? 'cargando'}`}>
      <div className="flex items-center justify-between text-muted-foreground text-sm"><span>{label}</span><Icono nombre={icon} className="size-4" aria-hidden /></div>
      <div className="text-3xl font-semibold tnum">{value === undefined ? <Skeleton className="h-8 w-12" /> : value}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </Link>
  );
}

/**
 * Lo primero del Inicio: el término legal de los expedientes a cargo de la persona (Ley 1755 o el del juez), no el
 * plazo de sus tareas. Vencidos, vencen hoy y los que vencen en los próximos tres días hábiles; aparte, cuántas tutelas.
 */
function TerminosSection() {
  const dash = useMyDashboard();
  const t = dash.data?.term;
  const tutelas = (n: number) => (n > 0 ? `${n} ${n === 1 ? 'tutela' : 'tutelas'}` : undefined);
  const nada = t && t.overdue === 0 && t.dueToday === 0 && t.dueSoon === 0;
  return (
    <section aria-labelledby="term-h" className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 id="term-h" className="text-base font-semibold m-0">Términos legales de mis expedientes</h2>
        {nada && <span className="text-sm text-success inline-flex items-center gap-1"><Icono nombre="mdi:check-circle-outline" aria-hidden /> Nada vencido ni por vencer en tres días hábiles</span>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="Vencidos" value={t?.overdue} tone="critical" to="/pqrs?view=mias&overdue=true" icon='mdi:alert-circle-outline' hint={tutelas(t?.judicialOverdue ?? 0) ?? 'Término legal cumplido sin respuesta'} />
        <StatCard label="Vencen hoy" value={t?.dueToday} tone="warning" to="/pqrs?view=mias&dueToday=true" icon='mdi:calendar-alert' hint="Responder hoy" />
        <StatCard label="Próximos 3 días hábiles" value={t?.dueSoon} tone="warning" to="/pqrs?view=mias&dueSoon=true" icon='mdi:clock-outline' hint={tutelas(t?.judicialDueSoon ?? 0) ?? 'Incluye los de hoy'} />
      </div>
    </section>
  );
}

function InstrumentadorSection() {
  const dash = useMyDashboard();
  const tasks = useTasks({ mine: true, limit: 50 });
  // Los apoyos que le piden a sus áreas son tareas más: van primero (son pocos y otra área espera la respuesta).
  const support = usePendingSupport().data ?? [];
  const nav = useNavigate();
  const d = dash.data;
  return (
    <section aria-labelledby="mine-h" className="flex flex-col gap-4">
      <h2 id="mine-h" className="text-base font-semibold m-0">Mi trabajo</h2>
      {dash.isError ? <ErrorState error={dash.error} onRetry={() => void dash.refetch()} title="No pudimos cargar tus indicadores." /> : (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
          <StatCard label="Pendientes" value={d?.pending} tone="info" to="/tareas" icon='mdi:check-circle-outline' />
          <StatCard label="Vencen hoy" value={d?.dueToday} tone="warning" to="/tareas?grupo=today" icon='mdi:clock-outline' />
          <StatCard label="Vencidas" value={d?.overdue} tone="critical" to="/tareas?grupo=overdue" icon='mdi:alert-circle-outline' />
          <StatCard label="En revisión" value={d?.inReview} tone="neutral" to="/pqrs?view=mias&status=WAITING_APPROVAL" icon='mdi:eye-outline' hint="Esperando aprobación" />
          <StatCard label="Bloqueadas" value={d?.blocked} tone="critical" to="/tareas?grupo=blocked" icon='mdi:lock-outline' />
          <StatCard label="Nuevas (24h)" value={d?.newToday} tone="ok" to="/tareas" icon='mdi:creation-outline' hint="Acaban de llegar" />
        </div>
      )}
      <Card title="Mis tareas" actions={<Link to="/tareas" className="text-sm text-primary hover:underline">Ver todas</Link>}>
        {tasks.isLoading ? <div className="flex flex-col gap-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          : tasks.isError ? <ErrorState error={tasks.error} onRetry={() => void tasks.refetch()} title="No pudimos cargar tus tareas." />
          : !tasks.data?.length && !support.length ? <EmptyState title="No tienes tareas pendientes." hint="Cuando recibas una nueva tarea aparecerá aquí." icon='mdi:check-decagram-outline' />
          : <ul className="list-none m-0 p-0 divide-y divide-border">
            {support.slice(0, 8).map((r) => <SupportTaskRow key={r.id} r={r} onOpen={() => nav(`/pqrs/${r.caseId}?tab=apoyos`)} />)}
            {(tasks.data ?? []).slice(0, Math.max(0, 8 - support.length)).map((t) => <TaskRow key={t.id} task={t} onOpen={() => nav(`/tareas/${t.id}`)} />)}
          </ul>}
      </Card>
    </section>
  );
}

export function TaskRow({ task, onOpen }: { task: Task; onOpen: () => void }) {
  const g = dueGroup(task.dueAt);
  const dot: { icono: NombreIcono; cls: string } = g.key === 'overdue' || g.key === 'today' ? { icono: 'mdi:alert-circle', cls: 'text-destructive' } : g.key === 'tomorrow' ? { icono: 'mdi:alert', cls: 'text-warning' } : task.status === 'BLOCKED' ? { icono: 'mdi:cancel', cls: 'text-destructive' } : { icono: 'mdi:clock-outline', cls: 'text-primary' };
  return (
    <li>
      <Item asChild size="sm" className="w-full flex-nowrap gap-3 py-2 px-1 text-left cursor-pointer hover:bg-accent">
        <button type="button" onClick={onOpen} aria-label={`${ACTION_LABEL[task.actionCode] ?? task.name} · ${task.radication ?? ''} · ${task.due?.text ?? 'sin fecha'}`}>
          <ItemMedia aria-hidden className={`text-base w-6 ${dot.cls}`}><Icono nombre={dot.icono} /></ItemMedia>
          <span className="w-44 shrink-0">
            <span className="text-xs text-muted-foreground block">{task.radication ?? '—'}</span>
            <span className="text-xs text-muted-foreground">{task.caseTypeName}</span>
          </span>
          <ItemContent className="min-w-0 gap-0">
            <ItemTitle className="w-full"><span className="truncate">{task.name}{task.status === 'BLOCKED' && <span className="ml-2 text-xs text-destructive font-normal">Bloqueada</span>}</span></ItemTitle>
            <ItemDescription className="line-clamp-1 text-[13px]">{task.subject}{task.specialtyName ? ` · ${task.specialtyName}` : ''}</ItemDescription>
          </ItemContent>
          <ItemActions className="gap-3">
            <span className="hidden md:inline">{task.priority && <PriorityTag priority={task.priority} />}</span>
            <span className="w-40 text-right text-[13px]"><DueBadge due={task.due} dueAt={task.dueAt} /></span>
          </ItemActions>
        </button>
      </Item>
    </li>
  );
}

/** Para quien lidera un área: los números de sus casos y la carga de cada área (con sus subáreas). */
function AreaSection() {
  const units = useUnitsTree();
  const [unitId, setUnitId] = useLocalStorage<string>('pqrs.dashboard.unit', '');
  const counts = useAreaCounts(unitId || undefined);
  const dist = useDistribution(unitId || undefined);
  const c = counts.data;
  const unitQ = unitId ? `&organizationalUnitId=${unitId}` : '';
  const TODOS = '__todos__';
  return (
    <section aria-labelledby="area-h" className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 id="area-h" className="text-base font-semibold m-0">Mi área</h2>
        <Select value={unitId || TODOS} onValueChange={(v) => setUnitId(v === TODOS ? '' : v)}>
          <SelectTrigger className="w-64" aria-label="Área"><SelectValue placeholder="Todas las áreas" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS}>Todas las áreas</SelectItem>
            {flattenUnits(units.data).map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {counts.isError ? <ErrorState error={counts.error} onRetry={() => void counts.refetch()} title="No pudimos cargar los indicadores del área." /> : (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
          <StatCard label="Sin asignar" value={c?.unassigned} tone="warning" to="/asignacion/sin-asignar" icon='mdi:account-remove-outline' />
          <StatCard label="Vencidos" value={c?.overdue} tone="critical" to={`/pqrs?overdue=true${unitQ}`} icon='mdi:alert-circle-outline' hint={c?.judicialOverdue ? `${c.judicialOverdue} ${c.judicialOverdue === 1 ? 'tutela' : 'tutelas'}` : undefined} />
          <StatCard label="Próximos a vencer" value={c?.dueSoon} tone="warning" to={`/pqrs?dueSoon=true${unitQ}`} icon='mdi:clock-outline' hint={c?.judicialDueSoon ? `3 días hábiles · ${c.judicialDueSoon} ${c.judicialDueSoon === 1 ? 'tutela' : 'tutelas'}` : 'Próximos 3 días hábiles'} />
          <StatCard label="Baja confianza" value={c?.lowConfidence} tone="info" to={`/pqrs?view=revisar${unitQ}`} icon='mdi:robot-outline' hint="Requieren revisión" />
          <StatCard label="Bloqueados" value={c?.blocked} tone="critical" to={`/pqrs?view=pendientes${unitQ}`} icon='mdi:lock-outline' />
          <StatCard label="Esperando aprobación" value={c?.waitingApproval} tone="info" to={`/pqrs?status=WAITING_APPROVAL${unitQ}`} icon='mdi:eye-outline' />
          <StatCard label="Nuevos hoy" value={c?.newToday} tone="ok" to={`/pqrs?sort=createdAt&dir=desc${unitQ}`} icon='mdi:creation-outline' />
        </div>
      )}
      <Card title="Carga por área" actions={<Link to="/asignacion" className="text-sm text-primary hover:underline">Gestionar asignación</Link>}>
        {dist.isLoading ? <div className="flex flex-col gap-2">{[1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          : dist.isError ? <ErrorState error={dist.error} onRetry={() => void dist.refetch()} title="No pudimos cargar la distribución." />
          : !dist.data?.length ? <EmptyState title="No hay áreas con personas." hint="Cuando agregues personas a las áreas (Configuración › Organigrama) verás aquí su capacidad." />
          : <div className="grid md:grid-cols-2 gap-4">{dist.data.map((t) => <AreaCapacity key={t.unit.id} t={t} />)}</div>}
      </Card>
    </section>
  );
}

export function CapacityBar({ usage, label }: { usage: number | null; label?: string }) {
  const pct = Math.max(0, Math.min(100, usage ?? 0));
  const tone = pct >= 90 ? 'bg-destructive' : pct >= 75 ? 'bg-warning' : 'bg-success';
  return (
    <div className="flex items-center gap-2" role="meter" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={label ?? 'Capacidad'}>
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden"><div className={`h-full ${tone}`} style={{ width: `${pct}%` }} /></div>
      <span className="text-xs w-12 text-right tnum">{usage === null ? '—' : `${pct}%`}</span>
    </div>
  );
}

function AreaCapacity({ t }: { t: ReturnType<typeof useDistribution>['data'] extends (infer U)[] | undefined ? U : never }) {
  return (
    <div className="border border-border rounded-lg p-3">
      <div className="flex items-baseline justify-between"><div className="font-medium">{t.unit.name}</div><div className="text-xs text-muted-foreground">{t.people.length} personas · {t.totalCases} casos activos</div></div>
      <div className="mt-2"><CapacityBar usage={t.usage} label={`Capacidad ${t.unit.name}`} /></div>
      <div className="mt-2 flex gap-4 text-[13px] flex-wrap">
        <Link to="/asignacion/sin-asignar" className="text-warning no-underline hover:underline">Sin asignar: <strong>{t.unassigned}</strong></Link>
        <Link to={`/pqrs?overdue=true&organizationalUnitId=${t.unit.id}`} className="text-destructive no-underline hover:underline">Vencidos: <strong>{t.overdue}</strong></Link>
        <span className="text-muted-foreground">Puntos: {t.totalPoints}/{t.totalCapacity}</span>
      </div>
    </div>
  );
}
